import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Building2, BookOpen, CreditCard, Star, LogOut, ArrowLeft, Menu } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/dashboard/institution", label: "My Institution", icon: Building2 },
  { to: "/dashboard/courses", label: "My Courses", icon: BookOpen },
  { to: "/dashboard/listing", label: "My Listing", icon: Star },
  { to: "/dashboard/payments", label: "Payments", icon: CreditCard },
];

function Nav({ onNavigate }) {
  return (
    <nav className="space-y-1">
      {LINKS.map((l) => (
        <NavLink key={l.to} to={l.to} end={l.end} onClick={onNavigate} data-testid={`dash-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
            isActive ? "bg-blue-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
          <l.icon className="h-4 w-4" /> {l.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const instName = user?.institution?.name || user?.name;

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col bg-slate-900 p-4 lg:flex">
        <Link to="/dashboard" className="mb-6 flex items-center px-1">
          <img src="/oed-logo.gif" alt="Online Education Directory" className="h-10 w-auto max-w-[180px] rounded bg-white p-1" />
        </Link>
        <div className="mb-4 rounded-lg bg-slate-800 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Institution</p>
          <p className="mt-0.5 truncate font-heading text-sm font-semibold text-white">{instName}</p>
        </div>
        <div className="flex-1 overflow-y-auto"><Nav /></div>
        <div className="border-t border-slate-800 pt-3">
          <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to site
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white" onClick={() => { logout(); navigate("/"); }} data-testid="dash-logout">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button variant="ghost" size="icon" data-testid="dash-mobile-menu"><Menu className="h-6 w-6" /></Button></SheetTrigger>
            <SheetContent side="left" className="w-64 bg-slate-900 p-4">
              <Link to="/dashboard" className="mb-6 mt-2 flex items-center">
                <img src="/oed-logo.gif" alt="OED" className="h-9 w-auto max-w-[170px] rounded bg-white p-1" />
              </Link>
              <Nav onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="truncate font-heading font-bold text-slate-900">{instName}</span>
        </header>
        <main className="p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
